import React from 'react'
import './mobile-aapl-iframe-demo.css'
import { computeLogicalViewport } from '../lib/playcanvas-harness/compute-logical-viewport'

/**
 * Same-origin development target. The host page carries only `mobileIframeDemo=true`
 * and this URL carries only `dummyAapl=true`, so the embedded document renders
 * `DummyAaplChart` and cannot recurse back into this host.
 */
const DUMMY_AAPL_URL = '/?dummyAapl=true'

const IFRAME_TITLE = 'AAPL chart simulation'

/**
 * Development-only, phone-facing simulation of the production scaled-iframe embedding.
 *
 * Mirrors `ChartViewAdapter`'s documented invariants — desktop-capable logical
 * dimensions, uniform top-left scale, navigation deferred until valid geometry, hide via
 * `visibility` rather than `display` — while measuring a plain DOM rectangle instead of
 * a PlayCanvas placeholder, so no canvas or global caller state is required.
 */
export function MobileAaplIframeDemo(): React.ReactElement {
  const rectRef = React.useRef<HTMLDivElement | null>(null)
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null)

  /** Cleared once assigned, so later geometry updates never renavigate the live chart. */
  const pendingSrcRef = React.useRef<string | undefined>(DUMMY_AAPL_URL)

  const [status, setStatus] = React.useState<'measuring' | 'loading' | 'navigated'>('measuring')

  React.useEffect(() => {
    const rectElement = rectRef.current

    if (!rectElement) return

    const applyGeometry = (): void => {
      const iframe = iframeRef.current

      if (!iframe) return

      // The absolutely positioned iframe fills the *padding* box, so measuring the border
      // box would overshoot by the rectangle's border width and clip the chart's right and
      // bottom edges. `clientWidth`/`clientHeight` are the padding box.
      const viewport = computeLogicalViewport(rectElement.clientWidth, rectElement.clientHeight)

      if (!viewport) {
        // Keep the element (and its logical size) in layout so a later valid rectangle
        // restores the same live chart without it ever measuring a zero-size viewport.
        iframe.style.visibility = 'hidden'
        iframe.style.pointerEvents = 'none'
        return
      }

      iframe.style.width = `${viewport.logicalWidth}px`
      iframe.style.height = `${viewport.logicalHeight}px`
      iframe.style.transform = `scale(${viewport.scale})`

      // Navigate only now that real logical dimensions are on the element, so the chart's
      // very first layout sees a desktop-capable viewport. Done at most once.
      if (pendingSrcRef.current) {
        iframe.src = pendingSrcRef.current
        pendingSrcRef.current = undefined
        setStatus('loading')
      }

      if (iframe.style.visibility !== 'visible') {
        iframe.style.visibility = 'visible'
        iframe.style.pointerEvents = 'auto'
      }
    }

    applyGeometry()

    // Observing the rectangle itself covers window resize, orientation change, and mobile
    // address-bar driven dynamic viewport changes without a separate resize listener.
    const observer = new ResizeObserver(() => {
      applyGeometry()
    })

    observer.observe(rectElement)

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <main className="mobile-aapl-demo" data-testid="mobile-aapl-iframe-demo">
      <header className="mobile-aapl-demo__header">
        <span className="mobile-aapl-demo__symbol">AAPL</span>
        <span className="mobile-aapl-demo__status" data-testid="mobile-aapl-iframe-demo-status">
          {status === 'measuring' && 'Measuring chart area…'}
          {status === 'loading' && 'Loading offline AAPL chart…'}
          {status === 'navigated' && 'Iframe navigation completed'}
        </span>
      </header>

      <div className="mobile-aapl-demo__rect" ref={rectRef} data-testid="mobile-aapl-iframe-demo-rect">
        <iframe
          ref={iframeRef}
          className="mobile-aapl-demo__iframe"
          title={IFRAME_TITLE}
          // Only reports that the browsing context finished navigating. It is not
          // evidence that the embedded widget initialized — the parent cannot observe
          // that from here, so the status text deliberately claims no more than this.
          onLoad={() => setStatus((current) => (current === 'loading' ? 'navigated' : current))}
        />
      </div>
    </main>
  )
}
